import CheckIcon from "@mui/icons-material/Check";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDataGridSettings } from "../DataGridSettingsProvider";
import { DiagnosticSettingsSlotProps } from "@/models/CustomColumnMenuProps";

type StatusOption = { value: number | string; label: string };

export default function StatusColorMenu(props: DiagnosticSettingsSlotProps) {
  const { t } = useTranslation();
  const { colorIntervals, setColorIntervals } = useDataGridSettings();
  const field = props.field;

  // Where we read options from. Prefer colDef.valueOptions; otherwise empty.
  const options: StatusOption[] = useMemo(
    () => (props as any)?.colDef?.valueOptions ?? [],
    [props],
  );

  // Build a quick lookup of current colors for each option value
  const initialColorByValue = useMemo(() => {
    const arr = colorIntervals[field] ?? [];
    const map = new Map<string, string>();
    for (const it of arr) {
      const min = it.min as any;
      const max = it.max as any;
      if (min === max && min !== null && min !== undefined && min !== "") {
        map.set(String(min), it.color);
      }
    }
    // Back-compat: if existing ranges are present, use that color for any option falling inside
    if (map.size === 0 && (colorIntervals[field]?.length ?? 0) > 0) {
      for (const opt of options) {
        const v = Number(opt.value);
        const match = (colorIntervals[field] ?? []).find(
          it =>
            it.min !== null &&
            it.max !== null &&
            !isNaN(Number(it.min)) &&
            !isNaN(Number(it.max)) &&
            Number(it.min) <= v &&
            v <= Number(it.max),
        );
        if (match) map.set(String(opt.value), match.color);
      }
    }
    return map;
  }, [colorIntervals, field, options]);

  const [localColors, setLocalColors] = useState<Map<string, string>>(initialColorByValue);
  const [isEditing, setIsEditing] = useState(false);

  const setColorFor = (valueKey: string, color: string) => {
    setLocalColors(prev => {
      const next = new Map(prev);
      if (!color) next.delete(valueKey);
      else next.set(valueKey, color);
      return next;
    });
    setIsEditing(true);
  };

  const removeColorFor = (valueKey: string) => {
    setLocalColors(prev => {
      const next = new Map(prev);
      next.delete(valueKey);
      return next;
    });
    setIsEditing(true);
  };

  const resetAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalColors(new Map());
    setIsEditing(true);
  };

  const save = (e: React.MouseEvent) => {
    e.stopPropagation();

    const intervals = Array.from(localColors.entries()).map(([valueKey, color]) => ({
      min: Number(valueKey),
      max: Number(valueKey),
      color,
      backgroundColor: color,
    }));

    setColorIntervals(prev => ({ ...prev, [field]: intervals }));
    setIsEditing(false);
  };

  return (
    <div>
      <MenuItem>
        <ListItemIcon>
          <ColorLensIcon fontSize="small" />
        </ListItemIcon>
        <Typography sx={{ flex: 1 }}>{t("faultOverview.colorIntervalsTitle", "Colors")}</Typography>

        <Tooltip title={t("common.reset", "Reset all") as string}>
          <span>
            <IconButton size="small" onClick={resetAll}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {isEditing && (
          <Tooltip title={t("common.save", "Save") as string}>
            <span>
              <IconButton onClick={save} color="success" size="small">
                <CheckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </MenuItem>

      {/* One row per singleSelect option */}
      {options.map(opt => {
        const key = String(opt.value);
        const color = localColors.get(key) ?? "#cccccc";
        return (
          <MenuItem key={key} sx={{ gap: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
              <Typography sx={{ flex: 1, pr: 1 }} title={String(opt.value)}>
                {opt.label}
              </Typography>

              {/* color input – stop propagation so DataGrid menu doesn’t close on type */}
              <TextField
                size="small"
                sx={{ width: 64 }}
                type="color"
                value={color}
                onKeyDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
                onChange={e => {
                  e.stopPropagation();
                  setColorFor(key, e.target.value);
                }}
                inputProps={{ "aria-label": `${opt.label} color` }}
              />

              <Tooltip title={t("common.clear", "Clear") as string}>
                <span>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      removeColorFor(key);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </MenuItem>
        );
      })}
    </div>
  );
}
